import React, { useCallback, useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import config from './config';

interface DiscoverOrganizationItem {
  organizationId: number;
  name: string;
  slug: string;
}

const ROLE_OPTIONS = ['Student', 'Parent', 'Teacher', 'Principal'];

const OrganizationOnboarding: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [query, setQuery] = useState('');
  const [organizations, setOrganizations] = useState<DiscoverOrganizationItem[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0);
  const [requestedRoleKey, setRequestedRoleKey] = useState('Student');
  const [requestMessage, setRequestMessage] = useState('');

  const getAccessToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getAccessToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getAccessToken]
  );

  const loadOrganizations = useCallback(async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError('');

      const response = await withAuth({
        method: 'get',
        url: `${apiBaseUrl}/discover`,
        params: searchTerm ? { query: searchTerm } : undefined
      });

      setOrganizations((response.data ?? []) as DiscoverOrganizationItem[]);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadOrganizations();
  }, [isAuthenticated, loadOrganizations]);

  const handleSubmitJoinRequest = async () => {
    if (!selectedOrganizationId) {
      setError('Please select an organization.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setNotice('');

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/join-requests`,
        data: {
          organizationId: selectedOrganizationId,
          requestedRoleKey,
          message: requestMessage.trim() || undefined
        }
      });

      setNotice('Your join request was submitted. The organization owner will review it.');
      setRequestMessage('');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to submit join request.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 900, margin: '30px auto', padding: '0 16px 30px' }}>
        <h2>Organization Setup</h2>
        <p>Please sign in to create or join an organization.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '30px auto', padding: '0 16px 30px' }}>
      <h2 style={{ marginBottom: 8 }}>Organization Setup</h2>
      <p style={{ color: '#4b5563', marginBottom: 20 }}>
        Create your own tutoring organization (paid path) or request to join an existing organization.
      </p>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ background: '#dcfce7', color: '#166534', padding: 12, borderRadius: 6, marginBottom: 12 }}>{notice}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>Create Organization (Paid)</h3>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 0 }}>
            This path is payment-gated in the full rollout. For now, use admin create organization flow.
          </p>
          <Link
            to="/manage-organizations"
            style={{ display: 'inline-block', background: '#111827', color: '#fff', borderRadius: 6, padding: '10px 14px', textDecoration: 'none', fontWeight: 600 }}
          >
            Go To Organization Admin
          </Link>
        </section>

        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>Join Existing Organization</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name or slug"
                style={{ flex: 1, padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
              <button
                onClick={() => loadOrganizations(query.trim() || undefined)}
                disabled={loading}
                style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 14px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                Search
              </button>
            </div>

            <select
              value={selectedOrganizationId}
              onChange={e => setSelectedOrganizationId(Number(e.target.value))}
              style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
            >
              <option value={0}>Select organization</option>
              {organizations.map(org => (
                <option key={org.organizationId} value={org.organizationId}>{org.name} ({org.slug})</option>
              ))}
            </select>

            <select
              value={requestedRoleKey}
              onChange={e => setRequestedRoleKey(e.target.value)}
              style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6 }}
            >
              {ROLE_OPTIONS.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>

            <textarea
              value={requestMessage}
              onChange={e => setRequestMessage(e.target.value)}
              placeholder="Optional note to organization owner"
              rows={3}
              style={{ padding: 10, border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical' }}
            />

            <button
              onClick={handleSubmitJoinRequest}
              disabled={saving}
              style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 14px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontWeight: 600 }}
            >
              Submit Join Request
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizationOnboarding;
